/**
 * LEARNING BATTERY
 *
 * "What did it change?"
 *
 * Learn is post-activation intelligence. Not reporting.
 * Not about proving a signal exists — that's Validate.
 * Learn is about understanding how influence accumulates,
 * mutates, and compounds once something exists in the world.
 *
 * Ten dimensions. Each produces a categorical state.
 * Rules-based. Not scored. States only.
 *
 * Validate asks: "Is it real?"
 * Learn asks:    "What did it change?"
 *
 * They must never blur.
 */

import { CATEGORIES } from './synthetic';


// ─── THE TEN DIMENSIONS ─────────────────────────────────────────

/** 1. Influence Accumulation Tracking
 * Measures how effects stack across time, contexts, and channels.
 * Shows whether something is compounding or just repeating. */
export type InfluenceState = 'accumulating' | 'plateauing' | 'saturated' | 'fading';

/** 2. Expectation Shift Detection
 * Detects changes in what audiences expect as normal.
 * Real impact shows up as expectation change, not clicks. */
export type ExpectationState = 'no-shift' | 'local-shift' | 'category-shift' | 'cultural-shift';

/** 3. Identity Adoption Signaling
 * Determines whether an idea moved from interest → identity.
 * Identity signals predict long-term value far better than engagement. */
export type IdentityState = 'transactional' | 'habitual' | 'identity-anchored';

/** 4. Behavioral Follow-Through Analysis
 * Tests whether observed interest translated into sustained behavior.
 * Separates symbolic participation from real change. */
export type BehavioralState = 'interest-only' | 'partial-adoption' | 'sustained-behavior';

/** 5. Cross-Activation Learning Transfer
 * Checks whether learnings from one activation improve the next.
 * This is where compounding ROI actually shows up. */
export type TransferState = 'no-transfer' | 'partial-transfer' | 'strong-transfer';

/** 6. Longitudinal Coherence Maintenance
 * Ensures meaning doesn't decay or fracture over time.
 * Prevents success from quietly undermining the original idea. */
export type CoherenceState = 'coherent' | 'diluting' | 'fragmenting' | 'reclaimed';

/** 7. Field Shape Change Analysis
 * Measures how the overall field reorganized because something existed.
 * The clearest proof that an experience mattered. */
export type FieldShapeState = 'no-field-change' | 'local-reconfiguration' | 'structural-reorientation';

/** 8. Opportunity Surface Expansion
 * Identifies whether future options increased or narrowed.
 * Great experiences make future moves easier, not harder. */
export type OpportunityState = 'constrained' | 'neutral' | 'expanded';

/** 9. Decay & Half-Life Modeling
 * Measures how long influence lasts after activity stops.
 * Separates compounding systems from one-off stunts. */
export type DecayState = 'ephemeral' | 'medium-lived' | 'durable';

/** 10. Learning Synthesis State
 * Aggregates all learning signals into non-numeric insight states.
 * This is what feeds Evolve. */
export type LearningSynthesisState = 'no-learning' | 'local-learning' | 'reusable-learning' | 'systemic-learning';


// ─── THE BATTERY ─────────────────────────────────────────────────

export interface LearnBattery {
  influence: InfluenceState;
  expectation: ExpectationState;
  identity: IdentityState;
  behavioral: BehavioralState;
  transfer: TransferState;
  coherence: CoherenceState;
  fieldShape: FieldShapeState;
  opportunity: OpportunityState;
  decay: DecayState;
  synthesis: LearningSynthesisState;
}

export type LearnDimension = keyof LearnBattery;

export const LEARN_DIMENSIONS: LearnDimension[] = [
  'influence', 'expectation', 'identity', 'behavioral', 'transfer',
  'coherence', 'fieldShape', 'opportunity', 'decay', 'synthesis',
];

export const LEARN_DIMENSION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];


// ─── LABELS & DESCRIPTIONS ──────────────────────────────────────

export const LEARN_DIMENSION_LABELS: Record<LearnDimension, string> = {
  influence: 'Influence Accumulation',
  expectation: 'Expectation Shift',
  identity: 'Identity Adoption',
  behavioral: 'Behavioral Follow-Through',
  transfer: 'Learning Transfer',
  coherence: 'Longitudinal Coherence',
  fieldShape: 'Field Shape Change',
  opportunity: 'Opportunity Surface',
  decay: 'Decay & Half-Life',
  synthesis: 'Learning Synthesis',
};

export const LEARN_DIMENSION_DESCRIPTIONS: Record<LearnDimension, string> = {
  influence: 'How effects stack across time, contexts, and channels',
  expectation: 'Changes in what audiences expect as normal',
  identity: 'Whether an idea moved from interest to identity',
  behavioral: 'Whether interest translated into sustained behavior',
  transfer: 'Whether learnings from one activation improve the next',
  coherence: 'Whether meaning decays or holds over time',
  fieldShape: 'How the overall field reorganized because something existed',
  opportunity: 'Whether future options increased or narrowed',
  decay: 'How long influence lasts after activity stops',
  synthesis: 'Aggregated learning state — what feeds Evolve',
};


// ─── STATE DISPLAY NAMES ─────────────────────────────────────────

export const LEARN_STATE_DISPLAY: Record<string, string> = {
  // Influence
  'accumulating': 'Accumulating',
  'plateauing': 'Plateauing',
  'saturated': 'Saturated',
  'fading': 'Fading',
  // Expectation
  'no-shift': 'No shift',
  'local-shift': 'Local shift',
  'category-shift': 'Category shift',
  'cultural-shift': 'Cultural shift',
  // Identity
  'transactional': 'Transactional',
  'habitual': 'Habitual',
  'identity-anchored': 'Identity-anchored',
  // Behavioral
  'interest-only': 'Interest only',
  'partial-adoption': 'Partial adoption',
  'sustained-behavior': 'Sustained behavior',
  // Transfer
  'no-transfer': 'No transfer',
  'partial-transfer': 'Partial transfer',
  'strong-transfer': 'Strong transfer',
  // Coherence
  'coherent': 'Coherent',
  'diluting': 'Diluting',
  'fragmenting': 'Fragmenting',
  'reclaimed': 'Reclaimed',
  // Field Shape
  'no-field-change': 'No field change',
  'local-reconfiguration': 'Local reconfiguration',
  'structural-reorientation': 'Structural reorientation',
  // Opportunity
  'constrained': 'Constrained',
  'neutral': 'Neutral',
  'expanded': 'Expanded',
  // Decay
  'ephemeral': 'Ephemeral',
  'medium-lived': 'Medium-lived',
  'durable': 'Durable',
  // Synthesis
  'no-learning': 'No learning captured',
  'local-learning': 'Local learning',
  'reusable-learning': 'Reusable learning',
  'systemic-learning': 'Systemic learning',
};


// ─── STATE CLASSIFICATION ────────────────────────────────────────

export type LearnStateClass = 'strong' | 'forming' | 'weak';

export const LEARN_STATE_CLASSES: Record<string, LearnStateClass> = {
  // Influence
  'accumulating': 'strong', 'plateauing': 'forming', 'saturated': 'weak', 'fading': 'weak',
  // Expectation
  'cultural-shift': 'strong', 'category-shift': 'strong', 'local-shift': 'forming', 'no-shift': 'weak',
  // Identity
  'identity-anchored': 'strong', 'habitual': 'forming', 'transactional': 'weak',
  // Behavioral
  'sustained-behavior': 'strong', 'partial-adoption': 'forming', 'interest-only': 'weak',
  // Transfer
  'strong-transfer': 'strong', 'partial-transfer': 'forming', 'no-transfer': 'weak',
  // Coherence
  'coherent': 'strong', 'reclaimed': 'forming', 'diluting': 'forming', 'fragmenting': 'weak',
  // Field Shape
  'structural-reorientation': 'strong', 'local-reconfiguration': 'forming', 'no-field-change': 'weak',
  // Opportunity
  'expanded': 'strong', 'neutral': 'forming', 'constrained': 'weak',
  // Decay
  'durable': 'strong', 'medium-lived': 'forming', 'ephemeral': 'weak',
  // Synthesis
  'systemic-learning': 'strong', 'reusable-learning': 'strong', 'local-learning': 'forming', 'no-learning': 'weak',
};


// ─── STATE → COLOR (for radial matrix cells) ────────────────────

export const LEARN_STATE_COLORS: Record<string, string> = {
  // Influence Accumulation
  'accumulating': '#50c8a0',
  'plateauing': '#dcb464',
  'saturated': '#c87850',
  'fading': '#b46464',
  // Expectation Shift
  'cultural-shift': '#3de8b4',
  'category-shift': '#50c8a0',
  'local-shift': '#dcb464',
  'no-shift': '#6a6a7a',
  // Identity Adoption
  'identity-anchored': '#50c8a0',
  'habitual': '#dcb464',
  'transactional': '#b46464',
  // Behavioral Follow-Through
  'sustained-behavior': '#50c8a0',
  'partial-adoption': '#dcb464',
  'interest-only': '#b46464',
  // Learning Transfer
  'strong-transfer': '#50c8a0',
  'partial-transfer': '#dcb464',
  'no-transfer': '#b46464',
  // Longitudinal Coherence
  'coherent': '#50c8a0',
  'reclaimed': '#8cb4dc',
  'diluting': '#dcb464',
  'fragmenting': '#b46464',
  // Field Shape Change
  'structural-reorientation': '#3de8b4',
  'local-reconfiguration': '#50c8a0',
  'no-field-change': '#6a6a7a',
  // Opportunity Surface
  'expanded': '#50c8a0',
  'neutral': '#6a6a7a',
  'constrained': '#b46464',
  // Decay & Half-Life
  'durable': '#50c8a0',
  'medium-lived': '#dcb464',
  'ephemeral': '#b46464',
  // Learning Synthesis
  'systemic-learning': '#3de8b4',
  'reusable-learning': '#50c8a0',
  'local-learning': '#dcb464',
  'no-learning': '#b46464',
};

// Color legend entries (unique colors used across all states)
export const LEARN_COLOR_LEGEND = [
  { color: '#3de8b4', label: 'Systemic / Structural' },
  { color: '#50c8a0', label: 'Strong signal' },
  { color: '#8cb4dc', label: 'Recovered / Reclaimed' },
  { color: '#dcb464', label: 'Forming / Partial' },
  { color: '#6a6a7a', label: 'No change / Neutral' },
  { color: '#c87850', label: 'Saturated' },
  { color: '#b46464', label: 'Weak / None' },
];


// ─── CLUSTER LEARN DATA ─────────────────────────────────────────

export interface ClusterLearning {
  clusterId: string;
  category: string;
  battery: LearnBattery;
}


// ─── DETERMINISTIC PRNG ─────────────────────────────────────────

function hashSeed(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) | 0;
    return (s >>> 0) / 4294967296;
  };
}

function pick<T>(rand: () => number, options: T[], weights: number[]): T {
  const total = weights.reduce((a, b) => a + b, 0);
  let r = rand() * total;
  for (let i = 0; i < options.length; i++) {
    r -= weights[i];
    if (r <= 0) return options[i];
  }
  return options[options.length - 1];
}


// ─── SYNTHETIC GENERATION ───────────────────────────────────────

export function generateLearnBatteries(): Map<string, ClusterLearning> {
  const result = new Map<string, ClusterLearning>();

  CATEGORIES.forEach((cat, catIdx) => {
    cat.clusters.forEach((cluster, clusterIdx) => {
      const id = `cluster-${catIdx}-${clusterIdx}`;
      const rand = seededRandom(hashSeed(id + '-learn-battery'));

      // Use cluster properties to bias distributions
      const w = cluster.weight;
      const isRising = cluster.trend === 'rising';
      const isStable = cluster.trend === 'stable';
      const isFading = cluster.trend === 'fading';
      const isEmerging = cluster.trend === 'emerging';

      // Higher weight + rising → stronger learn signals
      const learnBias = w * (isRising ? 1.2 : isEmerging ? 0.9 : isStable ? 0.7 : 0.4);

      // 1. Influence Accumulation
      const influence = pick<InfluenceState>(rand,
        ['accumulating', 'plateauing', 'saturated', 'fading'],
        [learnBias * 3, 2, isFading ? 3 : 1, isFading ? 4 : 0.5],
      );

      // 2. Expectation Shift
      const expectation = pick<ExpectationState>(rand,
        ['cultural-shift', 'category-shift', 'local-shift', 'no-shift'],
        [learnBias * 1.5, learnBias * 3, 3, isEmerging ? 4 : 2],
      );

      // 3. Identity Adoption
      const identity = pick<IdentityState>(rand,
        ['identity-anchored', 'habitual', 'transactional'],
        [learnBias * 2, 3, isEmerging || isFading ? 3 : 1],
      );

      // 4. Behavioral Follow-Through
      const behavioral = pick<BehavioralState>(rand,
        ['sustained-behavior', 'partial-adoption', 'interest-only'],
        [learnBias * 2.5, 3, isFading ? 3 : 1],
      );

      // 5. Cross-Activation Learning Transfer
      const transfer = pick<TransferState>(rand,
        ['strong-transfer', 'partial-transfer', 'no-transfer'],
        [learnBias * 1.5, 3, isEmerging ? 3 : 1.5],
      );

      // 6. Longitudinal Coherence
      const coherence = pick<CoherenceState>(rand,
        ['coherent', 'diluting', 'fragmenting', 'reclaimed'],
        [isStable || isRising ? 4 : 1, 2, isFading ? 3 : 0.5, 1.5],
      );

      // 7. Field Shape Change
      const fieldShape = pick<FieldShapeState>(rand,
        ['structural-reorientation', 'local-reconfiguration', 'no-field-change'],
        [w > 0.8 && isRising ? 3 : 0.5, learnBias * 3, 2],
      );

      // 8. Opportunity Surface
      const opportunity = pick<OpportunityState>(rand,
        ['expanded', 'neutral', 'constrained'],
        [learnBias * 3, 2, isFading ? 3 : 0.5],
      );

      // 9. Decay & Half-Life
      const decay = pick<DecayState>(rand,
        ['durable', 'medium-lived', 'ephemeral'],
        [isStable ? 4 : learnBias * 2, 3, isEmerging ? 3 : 1],
      );

      // 10. Learning Synthesis — rules-based from the 9 dimensions above
      let strongCount = 0;
      let weakCount = 0;
      const states: string[] = [influence, expectation, identity, behavioral, transfer, coherence, fieldShape, opportunity, decay];
      for (const state of states) {
        const cls = LEARN_STATE_CLASSES[state];
        if (cls === 'strong') strongCount++;
        if (cls === 'weak') weakCount++;
      }

      let synthesis: LearningSynthesisState;
      if (strongCount >= 7) synthesis = 'systemic-learning';
      else if (strongCount >= 5) synthesis = 'reusable-learning';
      else if (strongCount >= 2) synthesis = 'local-learning';
      else synthesis = 'no-learning';

      result.set(id, {
        clusterId: id,
        category: cat.name,
        battery: {
          influence,
          expectation,
          identity,
          behavioral,
          transfer,
          coherence,
          fieldShape,
          opportunity,
          decay,
          synthesis,
        },
      });
    });
  });

  return result;
}
